// OtherDeductions.js - Giao diện tạo khấu trừ động hàng loạt
import React, { useState, useEffect } from 'react';
import { apiGetGroups, apiGetEmployeesByGroup } from '../../api';

function OtherDeductions({ onSubmit, month: propMonth, year: propYear }) {
    const [form, setForm] = useState({ month: propMonth || '', year: propYear || '', amount: '', deductionName: '', note: '' });
    const [manualIds, setManualIds] = useState('');
    
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [activeTab, setActiveTab] = useState('select');

    useEffect(() => {
        setForm(f => ({ ...f, month: propMonth || '', year: propYear || '' }));
    }, [propMonth, propYear]);

    useEffect(() => {
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const data = await apiGetGroups();
                setGroups(data);
            } catch (err) {
                console.error("Lỗi tải bộ phận", err);
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchGroups();
    }, []);

    useEffect(() => {
        const fetchEmployees = async () => {
            if (!selectedGroup || selectedGroup === 'ALL' || !form.month || !form.year) {
                setEmployees([]);
                return;
            }
            setLoadingEmployees(true);
            try {
                const yymm = `${form.year}${String(form.month).padStart(2, '0')}`;
                const data = await apiGetEmployeesByGroup(selectedGroup, yymm);
                setEmployees(data || []);
            } catch (err) {
                console.error("Lỗi tải nhân viên", err);
            } finally {
                setLoadingEmployees(false);
            }
        };
        fetchEmployees();
    }, [selectedGroup, form.month, form.year]);

    const formatCurrency = (value) => {
        if (!value) return '';
        const number = Number(String(value).replace(/\D/g, ''));
        if (!number) return '';
        return number.toLocaleString('vi-VN');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'amount') {
            const raw = value.replace(/\D/g, '');
            setForm({ ...form, amount: raw ? formatCurrency(raw) : '' });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSelectEmployee = (empid, checked) => {
        if (checked) {
            setSelectedEmployees(prev => [...prev, empid]);
        } else {
            setSelectedEmployees(prev => prev.filter(id => id !== empid));
        }
    };

    const handleSelectAll = (checked) => {
        const currentEmpIds = employees.map(emp => emp.empid);
        if (checked) {
            setSelectedEmployees(prev => [...new Set([...prev, ...currentEmpIds])]);
        } else {
            setSelectedEmployees(prev => prev.filter(id => !currentEmpIds.includes(id)));
        }
    };

    const isAllSelected = employees.length > 0 && employees.every(emp => selectedEmployees.includes(emp.empid));

    const handleSubmit = (e) => {
        e.preventDefault();
        const amountNum = Number(String(form.amount).replace(/\D/g, ''));
        if (!amountNum) return alert('Vui lòng nhập số tiền.');
        if (!form.deductionName) return alert('Vui lòng nhập tên khoản trừ.');

        // FIX LỖI RỖNG: Lọc bỏ toàn bộ dấu phẩy, khoảng trắng, xuống dòng, và các mảng rỗng
        const typedIds = manualIds
            .split(/[\n\r,; \t]+/)
            .map(id => id.trim().toUpperCase())
            .filter(id => id && id.length >= 3); // Bắt buộc ID phải có thật và >= 3 ký tự

        const finalIds = [...new Set([...typedIds, ...selectedEmployees])];

        if (finalIds.length === 0) {
            return alert('Vui lòng chọn hoặc nhập ít nhất 1 mã nhân viên hợp lệ.');
        }

        const confirmMsg = `Thêm khoản trừ "${form.deductionName}" (${formatCurrency(amountNum)} đ) cho ${finalIds.length} nhân viên?`;
        if (!window.confirm(confirmMsg)) return;

        const payload = finalIds.map(empid => ({
            employeeId: empid,
            month: form.month,
            year: form.year,
            amount: amountNum,
            deductionName: form.deductionName,
            note: form.note
        }));

        if (onSubmit) {
            onSubmit(payload);
            setManualIds(''); 
            setSelectedEmployees([]);
            setForm(f => ({ ...f, amount: '', note: '' })); 
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-5 border border-gray-200 rounded-xl shadow-sm bg-white space-y-5">
            <h3 className="font-bold text-gray-800 border-b pb-2 text-lg">Tạo Khấu Trừ Hàng Loạt</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tên khoản trừ (Tiêu đề cột Excel)</label>
                    <input name="deductionName" placeholder="VD: Trừ thiên tai..." value={form.deductionName} onChange={handleChange} required className="border border-gray-300 p-2 w-full rounded focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div className="flex gap-2">
                    <div className="w-1/2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tháng</label>
                        <input name="month" placeholder="Tháng" value={form.month} onChange={handleChange} required className="border border-gray-300 p-2 w-full rounded text-center" />
                    </div>
                    <div className="w-1/2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Năm</label>
                        <input name="year" placeholder="Năm" value={form.year} onChange={handleChange} required className="border border-gray-300 p-2 w-full rounded text-center" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền (áp dụng chung)</label>
                    <input name="amount" placeholder="Nhập số tiền..." value={form.amount} onChange={handleChange} required className="border border-gray-300 p-2 w-full rounded font-bold text-red-600" inputMode="numeric" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú (tùy chọn)</label>
                    <input name="note" placeholder="Chi tiết..." value={form.note} onChange={handleChange} className="border border-gray-300 p-2 w-full rounded" />
                </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex border-b bg-gray-50">
                    <button type="button" onClick={() => setActiveTab('select')} className={`flex-1 py-2 text-sm font-semibold transition-colors ${activeTab === 'select' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>
                        ☑️ Chọn theo Bộ phận
                    </button>
                    <button type="button" onClick={() => setActiveTab('manual')} className={`flex-1 py-2 text-sm font-semibold transition-colors ${activeTab === 'manual' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>
                        📋 Dán mã từ Excel
                    </button>
                </div>

                <div className="p-4 bg-white">
                    {activeTab === 'select' && (
                        <div className="space-y-3">
                            <select 
                                value={selectedGroup} 
                                onChange={e => setSelectedGroup(e.target.value)}
                                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-blue-500"
                            >
                                <option value="">-- Chọn bộ phận --</option>
                                {groups.map(g => <option key={g.groupId} value={g.groupId}>{g.groupName} ({g.groupId})</option>)}
                            </select>

                            {loadingEmployees ? (
                                <div className="text-sm text-gray-500 py-4 text-center">Đang tải danh sách nhân viên...</div>
                            ) : employees.length > 0 ? (
                                <div className="border rounded-md">
                                    <div className="p-2 border-b bg-gray-50 flex items-center">
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={isAllSelected} 
                                                onChange={e => handleSelectAll(e.target.checked)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                                            />
                                            <span className="text-sm font-semibold">Chọn tất cả bộ phận này</span>
                                        </label>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto p-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {employees.map(emp => (
                                            <label key={emp.empid} className="flex items-center space-x-2 p-1.5 hover:bg-blue-50 rounded cursor-pointer transition-colors">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedEmployees.includes(emp.empid)}
                                                    onChange={e => handleSelectEmployee(emp.empid, e.target.checked)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                                                />
                                                <span className="text-sm text-gray-700 truncate">{emp.empid} - {emp.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ) : selectedGroup && (
                                <div className="text-sm text-amber-600 py-2">Không có dữ liệu nhân viên (Hãy đảm bảo đã chốt lương).</div>
                            )}
                        </div>
                    )}

                    {activeTab === 'manual' && (
                        <div>
                            <textarea 
                                placeholder="VD: LT0124&#10;LT0384&#10;LT0516" 
                                value={manualIds} 
                                onChange={e => setManualIds(e.target.value)} 
                                rows={5}
                                className="border border-gray-300 p-3 w-full rounded font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1 italic">Có thể dán nguyên 1 cột copy từ Excel vào đây.</p>
                        </div>
                    )}
                </div>
            </div>

            {(selectedEmployees.length > 0 || manualIds.length > 0) && (
                <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg flex items-center justify-between">
                    <span className="text-sm font-semibold text-blue-800">
                        Đã chọn sẵn {selectedEmployees.length} nhân viên (từ danh sách) + ID dán thêm.
                    </span>
                    {selectedEmployees.length > 0 && (
                        <button type="button" onClick={() => setSelectedEmployees([])} className="text-xs font-bold text-rose-600 hover:text-rose-800">
                            Xóa chọn tất cả
                        </button>
                    )}
                </div>
            )}

            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-3 rounded transition-colors shadow-md text-lg">
                Lưu Khấu Trừ
            </button>
        </form>
    );
}

export default OtherDeductions;